import moment from "moment";
import {
  ContractDurationType,
  ExtensionDurationUnit,
  type ContractDurationDto,
  type ContractExtensionDto,
  type SourcingEventDto,
} from "../../../../../autogen/bff-api";
import { isWholeExtensionNumber } from "../../../../../common/extension/extensionRules";
import { offersExtensions } from "../../../../../common/procurement/procedure";

const MONTHS_PER_YEAR = 12;

export interface ExtensionLength {
  unit: "year" | "month";
  total: number;
  parts: number[];
}

export const extensionMonths = (extension: Pick<ContractExtensionDto, "duration" | "unit">): number =>
  extension.unit === ExtensionDurationUnit.Years ? extension.duration * MONTHS_PER_YEAR : extension.duration;

export const announcedExtensions = (event: SourcingEventDto): ContractExtensionDto[] => {
  const options = event.eventData.options;
  const procedureType = event.eventContext.type === "Public" ? event.eventContext.procedureType : null;
  return options?.hasOptions && offersExtensions(procedureType, event.eventData.contractDuration)
    ? options.extensions ?? []
    : [];
};

export const announcedExtensionMonths = (event: SourcingEventDto): number =>
  announcedExtensions(event).reduce((sum, extension) => sum + extensionMonths(extension), 0);

export const baseContractMonths = (duration: ContractDurationDto | null | undefined): number | null => {
  if (duration?.type === ContractDurationType.PeriodBased) return duration.periodMonths ?? null;
  if (duration?.type === ContractDurationType.FixedDates && duration.startDate && duration.endDate) {
    return wholeMonthsCovering(duration.startDate, duration.endDate);
  }
  return null;
};

export const wholeMonthsCovering = (startDate: string, lastDay: string): number | null => {
  const start = moment.utc(startDate);
  if (moment.utc(lastDay).isBefore(start)) return null;
  const endExclusive = moment.utc(lastDay).add(1, "day");
  const calendarMonths =
    (endExclusive.year() - start.year()) * MONTHS_PER_YEAR + (endExclusive.month() - start.month());
  const completeMonths = start.clone().add(calendarMonths, "months").isAfter(endExclusive)
    ? calendarMonths - 1
    : calendarMonths;
  const lastCompleteMonthEnd = start.clone().add(completeMonths, "months");
  const daysLeft = endExclusive.diff(lastCompleteMonthEnd, "days");
  const daysInNextMonth = start.clone().add(completeMonths + 1, "months").diff(lastCompleteMonthEnd, "days");
  const months = daysLeft * 2 >= daysInNextMonth ? completeMonths + 1 : completeMonths;
  return Math.max(months, 1);
};

export const lengthWithExtensions = (
  baseMonths: number | null,
  extensions: ContractExtensionDto[],
): ExtensionLength | null => {
  if (extensions.length === 0) return null;
  const months = [...(baseMonths == null ? [] : [baseMonths]), ...extensions.map(extensionMonths)];
  const total = months.reduce((sum, part) => sum + part, 0);
  if (months.every((part) => part % MONTHS_PER_YEAR === 0)) {
    return {
      unit: "year",
      total: total / MONTHS_PER_YEAR,
      parts: months.map((part) => part / MONTHS_PER_YEAR),
    };
  }
  return { unit: "month", total, parts: months };
};

export const contractMonthsBeforeEachExtension = (
  contractMonths: number | null,
  extensions: { duration: number | null; unit: ExtensionDurationUnit }[],
): (number | null)[] => {
  let monthsBefore = contractMonths;
  return extensions.map(({ duration, unit }) => {
    const limit = monthsBefore;
    monthsBefore =
      monthsBefore != null && isWholeExtensionNumber(duration) ? monthsBefore + extensionMonths({ duration, unit }) : null;
    return limit;
  });
};
